import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/hakkimizda', label: t('nav.about') },
    { 
      path: '#', 
      label: t('nav.services'),
      hasDropdown: true,
      subLinks: [
        { path: '/hizmetler/serbest-depo', label: 'Serbest Depo' },
        { path: '/hizmetler/transit', label: 'Transit Hizmetleri' },
        { path: '/hizmetler/katma-degerli', label: 'Katma Değerli Hizmetler' }
      ]
    },
    { path: '/blog', label: t('nav.blog') },
    { path: '/iletisim', label: t('nav.contact') }
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#1a1f4d] text-white py-2 text-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-6">
              <a href="tel:02124463750" className="flex items-center gap-2 hover:text-[#8b1538] transition-colors whitespace-nowrap">
                <i className="ri-phone-line"></i>
                <span>(0212) 446 37 50</span>
              </a>
              <a href="https://wa.me/905324469672" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#8b1538] transition-colors whitespace-nowrap">
                <i className="ri-whatsapp-line"></i>
                <span>(0532) 446 96 72</span>
              </a>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex gap-2">
                <button onClick={() => changeLanguage('tr')} className={`px-2 py-1 rounded hover:bg-white/10 transition-colors whitespace-nowrap ${i18n.language === 'tr' ? 'bg-white/20' : ''}`}>TR</button>
                <button onClick={() => changeLanguage('en')} className={`px-2 py-1 rounded hover:bg-white/10 transition-colors whitespace-nowrap ${i18n.language === 'en' ? 'bg-white/20' : ''}`}>EN</button>
                <button onClick={() => changeLanguage('de')} className={`px-2 py-1 rounded hover:bg-white/10 transition-colors whitespace-nowrap ${i18n.language === 'de' ? 'bg-white/20' : ''}`}>DE</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-white/95'}`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img src="https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/99728b562c9b9993c2c423ddde30401b.png" alt="Piramit Antrepo" className="h-14" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <div key={link.path} className="relative group">
                  {link.hasDropdown ? (
                    <>
                      <button 
                        className={`text-[15px] font-medium hover:text-[#8b1538] transition-colors whitespace-nowrap cursor-pointer ${location.pathname.startsWith('/hizmetler') ? 'text-[#8b1538]' : 'text-gray-700'}`}
                      >
                        {link.label}
                        <i className="ri-arrow-down-s-line ml-1"></i>
                      </button>
                      <div className="absolute top-full left-0 mt-2 w-64 bg-white shadow-xl rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                        <div className="py-2">
                          {link.subLinks?.map((subLink) => (
                            <Link 
                              key={subLink.path}
                              to={subLink.path}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#8b1538] transition-colors whitespace-nowrap"
                            >
                              {subLink.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <Link 
                      to={link.path} 
                      className={`text-[15px] font-medium hover:text-[#8b1538] transition-colors whitespace-nowrap ${location.pathname === link.path ? 'text-[#8b1538]' : 'text-gray-700'}`}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <a href="https://wa.me/905324469672" target="_blank" rel="noopener noreferrer" className="px-4 py-2 text-[#25D366] border border-[#25D366] rounded-lg hover:bg-[#25D366] hover:text-white transition-all whitespace-nowrap">
                <i className="ri-whatsapp-line mr-2"></i>
                {t('nav.whatsapp')}
              </a>
              <Link to="/iletisim" className="px-5 py-2 bg-[#8b1538] text-white rounded-lg hover:bg-[#6d1029] transition-all whitespace-nowrap">
                {t('nav.getQuote')}
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-2xl text-gray-700 w-10 h-10 flex items-center justify-center"
            >
              <i className={isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t"
            >
              <div className="container mx-auto px-4 py-4">
                <nav className="flex flex-col gap-2">
                  {navLinks.map((link) => (
                    <div key={link.path}>
                      {link.hasDropdown ? (
                        <>
                          <button
                            onClick={() => setIsServicesOpen(!isServicesOpen)}
                            className="w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors whitespace-nowrap flex items-center justify-between"
                          >
                            {link.label}
                            <i className={`ri-arrow-${isServicesOpen ? 'up' : 'down'}-s-line`}></i>
                          </button>
                          {isServicesOpen && (
                            <div className="ml-4 mt-1 space-y-1">
                              {link.subLinks?.map((subLink) => (
                                <Link
                                  key={subLink.path}
                                  to={subLink.path}
                                  onClick={() => setIsMobileMenuOpen(false)}
                                  className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors whitespace-nowrap"
                                >
                                  {subLink.label}
                                </Link>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <Link
                          to={link.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors whitespace-nowrap"
                        >
                          {link.label}
                        </Link>
                      )}
                    </div>
                  ))}
                  <div className="flex flex-col gap-2 mt-4">
                    <a href="https://wa.me/905324469672" target="_blank" rel="noopener noreferrer" className="px-4 py-3 text-center text-[#25D366] border border-[#25D366] rounded-lg hover:bg-[#25D366] hover:text-white transition-all whitespace-nowrap">
                      <i className="ri-whatsapp-line mr-2"></i>
                      {t('nav.whatsapp')}
                    </a>
                    <Link to="/iletisim" onClick={() => setIsMobileMenuOpen(false)} className="px-4 py-3 text-center bg-[#8b1538] text-white rounded-lg hover:bg-[#6d1029] transition-all whitespace-nowrap">
                      {t('nav.getQuote')}
                    </Link>
                  </div>
                </nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}